/**
 * CareTeamManager Component
 *
 * Manages patient care team - providers at specific sites.
 * Features:
 * - View current care team members with provider/site info
 * - Add new providers via NPI lookup or local search
 * - Edit care team member roles and primary status
 * - Remove care team members
 * - Associate/Unassociate care team relationships
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  CloudDownload as CloudDownloadIcon,
  CloudUpload as CloudUploadIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  OpenInNew as OpenInNewIcon,
  Search as SearchIcon,
  Business as BusinessIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
} from '@mui/icons-material';
import { ElationPushDialog } from '../elation';
import { PatientService } from '../../services/patient.service';
import {
  PatientProviderSite,
  Provider,
  Site,
  NPILookupResult,
  CARE_TEAM_ROLES,
  CareTeamRole,
  getConfirmationIcon,
  getConfirmationColor,
  formatPhone,
} from '../../model/provider.types';
import { toast } from 'react-toastify';
import Helper from '../../utils/helper';
import WarningIcon from '@mui/icons-material/Warning';
import { isControlVisible } from '../patients/detail/comp-config';

const patientService = new PatientService();

interface CareTeamManagerProps {
  patientId: string;
  open: boolean;
  onClose: () => void;
  onUpdate?: () => void;
  onEditProvider?: (providerId: number) => void;
}

export const CareTeamManager: React.FC<CareTeamManagerProps> = ({
  patientId,
  open,
  onClose,
  onUpdate,
  onEditProvider,
}) => {
  // Care team state
  const [careTeam, setCareTeam] = useState<PatientProviderSite[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Add provider dialog state
  const [addDialogOpen, setAddDialogOpen] = useState(false);

  // Find or Add search state - matches Streamlit flow
  const [searchLastName, setSearchLastName] = useState('');
  const [searchFirstName, setSearchFirstName] = useState('');
  const [searchState, setSearchState] = useState('');
  const [hasSearched, setHasSearched] = useState(false);
  const [searching, setSearching] = useState(false);

  // Local database results (searched first)
  const [localSearchResults, setLocalSearchResults] = useState<Provider[]>([]);

  // NPI Registry results (searched second)
  const [npiSearchResults, setNpiSearchResults] = useState<NPILookupResult[]>([]);

  // Track which NPI results are already in local DB
  const [npisInLocalDb, setNpisInLocalDb] = useState<Set<string>>(new Set());

  // Add form state
  const [selectedProvider, setSelectedProvider] = useState<Provider | null>(null);
  const [selectedSite, setSelectedSite] = useState<Site | null>(null);
  const [selectedRole, setSelectedRole] = useState<CareTeamRole>('other');
  const [addNotes, setAddNotes] = useState('');
  const [saving, setSaving] = useState(false);

  // Edit dialog state
  const [editingMember, setEditingMember] = useState<PatientProviderSite | null>(null);
  const [editRole, setEditRole] = useState<CareTeamRole>('other');
  const [editNotes, setEditNotes] = useState('');

  // Change Site state
  const [changeSiteMemberId, setChangeSiteMemberId] = useState<number | null>(null);
  const [providerSites, setProviderSites] = useState<Site[]>([]);
  const [loadingProviderSites, setLoadingProviderSites] = useState(false);

  // Elation push dialog state
  const [elationPushOpen, setElationPushOpen] = useState(false);

  // Site search state (when provider has no linked sites)
  const [siteSearchQuery, setSiteSearchQuery] = useState('');
  const [siteSearchResults, setSiteSearchResults] = useState<Site[]>([]);
  const [siteSearching, setSiteSearching] = useState(false);

  // Import care team state
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<any>(null);

  // Load care team
  const loadCareTeam = useCallback(async () => {
    if (!patientId) return;

    try {
      setLoading(true);
      setError(null);
      const response = await patientService.getPatientCareTeam(patientId);
      setCareTeam(response.data?.care_team || response.data || []);
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to load care team');
      setCareTeam([]);
    } finally {
      setLoading(false);
    }
  }, [patientId]);

  useEffect(() => {
    if (open && patientId) {
      loadCareTeam();
    }
  }, [open, patientId, loadCareTeam]);

  // Import care team from Elation Surescripts CCDA documents
  const handleImportCareTeam = async () => {
    try {
      setImporting(true);
      setImportResult(null);

      const response = await patientService.importCareTeamFromElation(patientId);
      const result = response.data;

      setImportResult(result);

      if (result.care_team_links_added > 0) {
        toast.success(
          `Imported ${result.care_team_links_added} care team member(s)`,
          Helper.bottom_center()
        );
        // Reload care team to show new members
        loadCareTeam();
        onUpdate?.();
      } else if (result.encounters_found === 0) {
        toast.info('No encounter data found in Elation', Helper.bottom_center());
      } else {
        toast.info('No new care team members to import', Helper.bottom_center());
      }
    } catch (err: any) {
      const message = err.response?.data?.error?.message || 'Import failed';
      toast.error(message, Helper.bottom_center());
      setImportResult(err.response?.data?.details || null);
    } finally {
      setImporting(false);
    }
  };

  // Two-step provider search: Local DB first, then NPI Registry
  const handleProviderSearch = async () => {
    if (!searchLastName.trim()) {
      toast.warning('Last name is required', Helper.bottom_center());
      return;
    }

    try {
      setSearching(true);
      setHasSearched(true);
      setLocalSearchResults([]);
      setNpiSearchResults([]);
      setNpisInLocalDb(new Set());
      setSelectedProvider(null);
      setSelectedSite(null);

      // Build search query for local DB
      const query = searchFirstName.trim()
        ? `${searchFirstName.trim()} ${searchLastName.trim()}`
        : searchLastName.trim();

      // Step 1: Search local database first
      try {
        const localResponse = await patientService.searchProvidersLocal(query, 20);
        const localProviders = localResponse.data?.providers || [];
        setLocalSearchResults(localProviders);

        // Build set of NPIs in local DB for quick lookup
        const localNpis = new Set<string>();
        localProviders.forEach((p: Provider) => {
          if (p.npi) localNpis.add(p.npi);
        });
        setNpisInLocalDb(localNpis);
      } catch (err: any) {
        console.error('Local search failed:', err);
        // Continue to NPI search even if local fails
      }

      // Step 2: Search NPI Registry
      try {
        const npiParams: any = {
          last_name: searchLastName.trim(),
          limit: 15,
        };
        if (searchFirstName.trim()) {
          npiParams.first_name = searchFirstName.trim();
        }
        if (searchState.trim()) {
          npiParams.state = searchState.trim().toUpperCase();
        }

        const npiResponse = await patientService.searchNPI(npiParams);
        setNpiSearchResults(npiResponse.data?.results || []);
      } catch (err: any) {
        console.error('NPI search failed:', err);
      }
    } finally {
      setSearching(false);
    }
  };

  // Select a local provider (already in DB)
  const handleSelectLocalProvider = (provider: Provider) => {
    setSelectedProvider(provider);
    // If provider has sites, select the first one
    if (provider.sites && provider.sites.length > 0) {
      setSelectedSite(provider.sites[0] as Site);
    } else {
      setSelectedSite(null);
    }
  };

  // Select NPI result and create provider format
  const handleSelectNpiResult = (result: NPILookupResult) => {
    // Convert NPI result to provider format
    const provider: Provider = {
      id: 0, // Will be created on save
      npi: result.npi,
      first_name: result.first_name || '',
      last_name: result.last_name || result.organization_name || '',
      credential: result.credential,
      specialty: result.taxonomy_description || '',
      taxonomy_code: result.taxonomy_code,
      taxonomy_display: result.taxonomy_description,
    };
    setSelectedProvider(provider);

    // Create site from NPI address if available
    if (result.address_1) {
      const site: Site = {
        id: 0,
        name: result.organization_name || `${result.first_name} ${result.last_name}`.trim(),
        address: result.address_1,
        address_2: result.address_2,
        city: result.city,
        state: result.state,
        zip_code: result.postal_code,
        phone: result.phone,
        fax: result.fax,
      };
      setSelectedSite(site);
    }
  };

  // Add care team member
  const handleAddMember = async () => {
    if (!selectedProvider) {
      toast.error('Please select a provider', Helper.bottom_center());
      return;
    }

    try {
      setSaving(true);

      // First, create provider if it's from NPI (id === 0)
      let providerId = selectedProvider.id;
      let siteId = selectedSite?.id || 0;

      if (providerId === 0 && selectedProvider) {
        try {
          // Try to create provider from NPI data
          const providerResponse = await patientService.createProvider({
            first_name: selectedProvider.first_name,
            last_name: selectedProvider.last_name,
            specialty: selectedProvider.specialty || '',
            npi: selectedProvider.npi,
            credential: selectedProvider.credential,
            taxonomy_code: selectedProvider.taxonomy_code,
            taxonomy_display: selectedProvider.taxonomy_display,
          });
          providerId = providerResponse.data?.id || providerResponse.data?.data?.id;
        } catch (createErr: any) {
          // If 409 conflict (provider already exists), try to get existing provider by NPI
          if (createErr.response?.status === 409 && selectedProvider.npi) {
            const existingProvider = await patientService.getProviderByNPI(selectedProvider.npi);
            providerId = existingProvider.data?.id || existingProvider.data?.data?.id;
          } else {
            throw createErr;
          }
        }
      }

      if (!providerId) {
        throw new Error('Failed to create or find provider');
      }

      // Create site if needed
      if (siteId === 0 && selectedSite) {
        try {
          const siteResponse = await patientService.addSite({
            name: selectedSite.name,
            address: selectedSite.address,
            city: selectedSite.city,
            state: selectedSite.state,
            zip_code: selectedSite.zip_code,
          });
          siteId = siteResponse.data?.id || siteResponse.data?.data?.id;
        } catch (siteErr: any) {
          // If site creation fails with conflict, we need to find or create differently
          // For now, just throw
          throw siteErr;
        }
      }

      // A missing site is NOT an error. Providers with no linked site, and NPI results
      // carrying no practice address, are both legitimate — the membership is
      // patient <-> provider and the site is optional detail (RD 2026-08-08). Omitting
      // the key entirely lets the backend store SQL NULL instead of a dangling site 0.
      await patientService.addCareTeamMember(patientId, {
        provider_id: providerId,
        ...(siteId ? { site_id: siteId } : {}),
        role: selectedRole,
        is_primary: selectedRole === 'pcp',
        // Manually entered members are associated by definition — there is no import
        // step to associate them against. The ○/Associate affordance only earns its
        // keep on the Surescripts path, where names arrive unattached.
        is_validated: true,
        notes: addNotes || undefined,
      });

      toast.success('Provider added to care team', Helper.bottom_center());
      handleCloseAddDialog();
      loadCareTeam();
      onUpdate?.();
    } catch (err: any) {
      toast.error(err.message || 'Failed to add provider', Helper.bottom_center());
    } finally {
      setSaving(false);
    }
  };

  // Update care team member
  const handleUpdateMember = async () => {
    if (!editingMember) return;

    try {
      setSaving(true);
      // Set is_primary true if role is PCP
      await patientService.updateCareTeamMember(editingMember.id, {
        role: editRole,
        is_primary: editRole === 'pcp',
        notes: editNotes || undefined,
      });

      toast.success('Care team member updated', Helper.bottom_center());
      setEditingMember(null);
      loadCareTeam();
      onUpdate?.();
    } catch (err: any) {
      toast.error('Failed to update', Helper.bottom_center());
    } finally {
      setSaving(false);
    }
  };

  // Remove care team member
  const handleRemoveMember = async (member: PatientProviderSite) => {
    if (!window.confirm(`Remove ${member.provider?.first_name} ${member.provider?.last_name} from care team?`)) {
      return;
    }

    try {
      await patientService.removeCareTeamMember(member.id);
      toast.success('Removed from care team', Helper.bottom_center());
      loadCareTeam();
      onUpdate?.();
    } catch (err: any) {
      toast.error('Failed to remove', Helper.bottom_center());
    }
  };

  // Open edit dialog
  const handleEditMember = (member: PatientProviderSite) => {
    setEditingMember(member);
    setEditRole(member.role || 'other');
    setEditNotes(member.notes || '');
  };

  // Toggle association status - can associate or unassociate
  const handleToggleAssociation = async (member: PatientProviderSite) => {
    const newAssociatedState = !member.is_validated;
    try {
      setSaving(true);
      await patientService.updateCareTeamMember(member.id, {
        is_validated: newAssociatedState,
      });

      toast.success(
        newAssociatedState ? 'Provider associated with patient' : 'Provider unassociated from patient',
        Helper.bottom_center()
      );
      loadCareTeam();
      onUpdate?.();
    } catch (err: any) {
      toast.error('Failed to update association status', Helper.bottom_center());
    } finally {
      setSaving(false);
    }
  };

  // Toggle the referring-physician star. Setting it clears the flag on the patient's
  // other memberships server-side, so the star behaves like a radio button and moving it
  // is a single call (RD 2026-08-08).
  const handleToggleReferring = async (member: PatientProviderSite) => {
    try {
      setSaving(true);
      await patientService.updateCareTeamMember(member.id, {
        is_referring: !member.is_referring,
      });
      loadCareTeam();
      onUpdate?.();
    } catch (err: any) {
      toast.error('Failed to update referring physician', Helper.bottom_center());
    } finally {
      setSaving(false);
    }
  };

  // Toggle Change Site panel for a member
  const handleToggleChangeSite = async (member: PatientProviderSite) => {
    if (changeSiteMemberId === member.id) {
      // Close the panel
      setChangeSiteMemberId(null);
      setProviderSites([]);
    } else {
      // Open the panel and load provider's sites
      setChangeSiteMemberId(member.id);

      if (member.provider?.id) {
        try {
          setLoadingProviderSites(true);
          const response = await patientService.getProviderSites(member.provider.id);
          setProviderSites(response.data?.sites || response.data || []);
        } catch (err: any) {
          console.error('Failed to load provider sites:', err);
          setProviderSites([]);
        } finally {
          setLoadingProviderSites(false);
        }
      }
    }
  };

  // Update site for a care team member
  const handleChangeSite = async (memberId: number, newSiteId: number) => {
    try {
      setSaving(true);
      await patientService.updateCareTeamMember(memberId, {
        site_id: newSiteId,
        // Reset validation when site changes
        is_validated: false,
      });

      toast.success('Site updated', Helper.bottom_center());
      setChangeSiteMemberId(null);
      setProviderSites([]);
      loadCareTeam();
      onUpdate?.();
    } catch (err: any) {
      toast.error('Failed to update site', Helper.bottom_center());
    } finally {
      setSaving(false);
    }
  };

  // Search for sites (when selected provider has no linked sites)
  const handleSiteSearch = async () => {
    if (!siteSearchQuery.trim() || siteSearchQuery.length < 2) {
      return;
    }

    try {
      setSiteSearching(true);
      const response = await patientService.searchSites({ query: siteSearchQuery, limit: 20 });
      setSiteSearchResults(response.data?.sites || response.data || []);
    } catch (err: any) {
      console.error('Site search failed:', err);
      toast.error('Site search failed', Helper.bottom_center());
    } finally {
      setSiteSearching(false);
    }
  };

  // Close add dialog and reset state
  const handleCloseAddDialog = () => {
    setAddDialogOpen(false);
    // Reset search state
    setSearchLastName('');
    setSearchFirstName('');
    setSearchState('');
    setHasSearched(false);
    setLocalSearchResults([]);
    setNpiSearchResults([]);
    setNpisInLocalDb(new Set());
    // Reset selection state
    setSelectedProvider(null);
    setSelectedSite(null);
    setSelectedRole('other');
    setAddNotes('');
    // Reset site search state
    setSiteSearchQuery('');
    setSiteSearchResults([]);
  };

  // Render site row with name, address, phone/fax with confirmation icons
  // Format: **SiteName** | Address | Ph ✓`phone` | **Fax ○`fax`**
  // Only shows site name if it differs from provider name
  const renderSiteRow = (site: Site, provider?: Provider) => {
    // Check if site name differs from provider name
    const providerName = provider
      ? `${provider.first_name || ''} ${provider.last_name || ''}`.trim().toUpperCase()
      : '';
    const siteNameUpper = (site.name || '').toUpperCase();
    const showSiteName = site.name && siteNameUpper !== providerName;

    // Get site name (truncate if needed)
    let siteName = site.name || '';
    if (siteName.length > 30) {
      siteName = siteName.slice(0, 27) + '...';
    }

    // Build address string: address_1, city, state
    const address = site.address || site.address_1 || '';
    let addressStr = '';
    if (address) {
      addressStr = address;
      if (site.city && site.state) {
        addressStr += `, ${site.city}, ${site.state}`;
      }
    }

    // Phone/fax with confirmation icons
    const phoneIcon = getConfirmationIcon(site.phone_confirmed_at);
    const phoneColor = getConfirmationColor(site.phone_confirmed_at);
    const faxIcon = getConfirmationIcon(site.fax_confirmed_at);
    const faxColor = getConfirmationColor(site.fax_confirmed_at);
    const phoneVal = site.phone ? formatPhone(site.phone) : '—';
    const faxVal = site.fax ? formatPhone(site.fax) : '—';

    // Render all on one line:
    // **SiteName** | Address | Ph ✓`phone` | **Fax ○`fax`**
    // Skip site name if it matches provider name
    return (
      <Box mt={0.5}>
        <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap">
          {site.is_placeholder && (
            <Tooltip title="Placeholder site - needs resolution">
              <WarningIcon sx={{ fontSize: 14, color: 'warning.main' }} />
            </Tooltip>
          )}
          <BusinessIcon fontSize="small" sx={{ fontSize: 14 }} color="action" />
          {showSiteName && (
            <>
              <Typography
                variant="caption"
                sx={{ fontWeight: site.is_placeholder ? 400 : 600, fontStyle: site.is_placeholder ? 'italic' : 'normal' }}
              >
                {siteName}
              </Typography>
              <Typography variant="caption" color="textSecondary">|</Typography>
            </>
          )}
          {addressStr && (
            <>
              <Typography variant="caption" color="textSecondary">
                {addressStr}
              </Typography>
              <Typography variant="caption" color="textSecondary">|</Typography>
            </>
          )}
          {/* Phone with confirmation icon */}
          <Typography
            variant="caption"
            sx={{
              color: phoneColor === 'success' ? 'success.main' : phoneColor === 'warning' ? 'warning.main' : 'text.disabled',
            }}
          >
            Ph {phoneIcon}
            <code style={{ fontSize: '0.7rem', marginLeft: 2 }}>{phoneVal}</code>
          </Typography>
          <Typography variant="caption" color="textSecondary">|</Typography>
          {/* Fax with confirmation icon - BOLD per requirements */}
          <Typography
            variant="caption"
            sx={{
              fontWeight: 600,
              color: faxColor === 'success' ? 'success.main' : faxColor === 'warning' ? 'warning.main' : 'text.disabled',
            }}
          >
            Fax {faxIcon}
            <code style={{ fontSize: '0.7rem', fontWeight: 600, marginLeft: 2 }}>{faxVal}</code>
          </Typography>
        </Stack>
      </Box>
    );
  };

  // Render care team member
  const renderMember = (member: PatientProviderSite) => {
    const provider = member.provider;
    const site = member.site;
    const displayName = provider?.display_name ||
      `${provider?.first_name || ''} ${provider?.last_name || ''}`.trim() || 'Unknown';

    // Association status icon. Only meaningful where providers arrive unattached from
    // an import; a hand-entered care team has nothing to associate against.
    const showAssociation = isControlVisible('care_team_association');
    const associationIcon = member.is_validated ? '✓' : '○';

    return (
      <Card key={member.id} variant="outlined" sx={{ mb: 1 }}>
        <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
            <Box flex={1}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="body2" fontWeight={500}>
                  {showAssociation ? `${associationIcon} ` : ''}{displayName}
                  {provider?.credential && `, ${provider.credential}`}
                </Typography>
                {member.is_primary && (
                  <Chip
                    icon={<StarIcon fontSize="small" />}
                    label="Primary"
                    size="small"
                    color="primary"
                    sx={{ height: 20 }}
                  />
                )}
                {/* Referring physician — a flag on the membership, so it composes with
                    whatever specialty this provider holds. */}
                <Tooltip title={member.is_referring ? 'Referring physician — click to clear' : 'Mark as the referring physician'}>
                  <IconButton
                    size="small"
                    onClick={() => handleToggleReferring(member)}
                    disabled={saving}
                    sx={{ p: 0.25 }}
                  >
                    {member.is_referring
                      ? <StarIcon fontSize="small" sx={{ color: 'var(--color-primary)' }} />
                      : <StarBorderIcon fontSize="small" sx={{ color: 'var(--color-text-secondary)' }} />}
                  </IconButton>
                </Tooltip>
                {member.is_referring && (
                  <Typography variant="caption" sx={{ color: 'var(--color-primary)' }}>
                    Referring
                  </Typography>
                )}
              </Stack>
              <Typography variant="caption" color="textSecondary" display="block">
                {provider?.specialty || provider?.taxonomy_display || 'Unknown specialty'}
                {' | '}
                {member.role ? member.role.replace(/_/g, ' ') : 'care team'}
              </Typography>

              {/* Full site info with phone/fax confirmation icons */}
              {site ? renderSiteRow(site, provider) : (
                <Typography variant="caption" color="textSecondary" mt={0.5} display="block">
                  No site selected for this provider
                </Typography>
              )}
            </Box>
            <Stack direction="row" spacing={0.5} alignItems="center">
              {/* Associate/Unassociate toggle button */}
              {showAssociation && (
                <Tooltip title={member.is_validated ? "Unassociate (e.g., no longer seeing this provider)" : "Associate this provider with the patient"}>
                  <Button
                    size="small"
                    variant={member.is_validated ? "outlined" : "contained"}
                    color={member.is_validated ? "inherit" : "primary"}
                    sx={{ minWidth: 'auto', px: 1.5, py: 0.5, fontSize: '0.75rem' }}
                    onClick={() => handleToggleAssociation(member)}
                  >
                    {member.is_validated ? 'Unassociate' : 'Associate'}
                  </Button>
                </Tooltip>
              )}
              <Tooltip title="Change site">
                <Button
                  size="small"
                  variant={changeSiteMemberId === member.id ? "contained" : "outlined"}
                  color="inherit"
                  sx={{ minWidth: 'auto', px: 1, py: 0.5, fontSize: '0.7rem' }}
                  onClick={() => handleToggleChangeSite(member)}
                >
                  Change Site
                </Button>
              </Tooltip>
              <Tooltip title="Edit role">
                <IconButton size="small" onClick={() => handleEditMember(member)}>
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              {onEditProvider && member.provider?.id && (
                <Tooltip title="Edit provider details">
                  <IconButton
                    size="small"
                    onClick={() => {
                      onEditProvider(member.provider!.id);
                      onClose();
                    }}
                  >
                    <OpenInNewIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              <Tooltip title="Remove">
                <IconButton size="small" onClick={() => handleRemoveMember(member)}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
          </Stack>

          {/* Change Site Panel */}
          {changeSiteMemberId === member.id && (
            <Box mt={2} pt={2} borderTop="1px solid" borderColor="divider">
              {renderChangeSitePanel(member)}
            </Box>
          )}
        </CardContent>
      </Card>
    );
  };

  // Render Change Site panel with provider's sites only
  const renderChangeSitePanel = (member: PatientProviderSite) => {
    const currentSiteId = member.site?.id;

    // Build site label: Name | Address - City, State
    const buildSiteLabel = (site: Site) => {
      const addr = site.address || site.address_1 || '';
      const addrParts: string[] = [];
      if (addr) addrParts.push(addr);
      if (site.city && site.state) {
        addrParts.push(`${site.city}, ${site.state}`);
      } else if (site.city) {
        addrParts.push(site.city);
      }
      const addrStr = addrParts.length ? addrParts.join(' - ') : 'No address';
      return `${site.name} | ${addrStr}`;
    };

    return (
      <Box>
        <Typography variant="subtitle2" gutterBottom>
          Select Site for this Provider
        </Typography>

        {loadingProviderSites ? (
          <CircularProgress size={16} />
        ) : providerSites.length > 0 ? (
          <FormControl fullWidth size="small">
            <Select
              value={currentSiteId || ''}
              onChange={(e) => {
                const newSiteId = e.target.value as number;
                if (newSiteId && newSiteId !== currentSiteId) {
                  handleChangeSite(member.id, newSiteId);
                }
              }}
            >
              {providerSites.map((site) => (
                <MenuItem key={site.id} value={site.id}>
                  {buildSiteLabel(site)}
                  {site.id === currentSiteId && ' (current)'}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        ) : (
          <Typography variant="caption" color="textSecondary">
            No other sites available for this provider
          </Typography>
        )}

        <Box mt={1}>
          <Button
            size="small"
            variant="text"
            onClick={() => {
              setChangeSiteMemberId(null);
              setProviderSites([]);
            }}
          >
            Cancel
          </Button>
        </Box>
      </Box>
    );
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Manage Care Team</Typography>
            <Stack direction="row" spacing={1}>
              {!Helper.isAIDA() && (
                <>
                  <Tooltip title="Import care team from Elation Surescripts encounter data">
                    <Button
                      startIcon={importing ? <CircularProgress size={16} /> : <CloudDownloadIcon />}
                      variant="outlined"
                      size="small"
                      onClick={handleImportCareTeam}
                      disabled={importing}
                    >
                      {importing ? 'Importing...' : 'Import from Elation'}
                    </Button>
                  </Tooltip>
                  <Button
                    startIcon={<CloudUploadIcon />}
                    variant="outlined"
                    size="small"
                    onClick={() => setElationPushOpen(true)}
                  >
                    Push to Elation
                  </Button>
                </>
              )}
              <Button
                startIcon={<AddIcon />}
                variant="contained"
                size="small"
                onClick={() => setAddDialogOpen(true)}
              >
                Add Provider
              </Button>
            </Stack>
          </Stack>
        </DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {/* Import result summary */}
          {importResult && (
            <Alert
              severity={importResult.care_team_links_added > 0 ? 'success' : 'info'}
              onClose={() => setImportResult(null)}
              sx={{ mb: 2 }}
            >
              <Typography variant="body2" fontWeight={500}>
                Import Complete
              </Typography>
              <Typography variant="caption" component="div">
                {importResult.encounters_found} encounters found •
                {importResult.providers_added} new providers •
                {importResult.sites_added} new sites •
                {importResult.care_team_links_added} care team links added
                {importResult.placeholders_created > 0 && ` • ${importResult.placeholders_created} placeholders`}
              </Typography>
            </Alert>
          )}

          {loading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : careTeam.length === 0 ? (
            <Box textAlign="center" py={4}>
              <Typography color="textSecondary">No care team members</Typography>
              <Button
                startIcon={<AddIcon />}
                onClick={() => setAddDialogOpen(true)}
                sx={{ mt: 2 }}
              >
                Add First Provider
              </Button>
            </Box>
          ) : (
            <Box>
              {[...careTeam]
                .sort((a, b) => {
                  // 1. Associated (is_validated) first
                  if (a.is_validated !== b.is_validated) {
                    return a.is_validated ? -1 : 1;
                  }

                  // 2. Organizations (no first_name) at bottom within each group
                  const aIsOrg = !a.provider?.first_name;
                  const bIsOrg = !b.provider?.first_name;
                  if (aIsOrg !== bIsOrg) {
                    return aIsOrg ? 1 : -1;
                  }

                  // 3. Alphabetical by last name
                  const aLastName = (a.provider?.last_name || '').toLowerCase();
                  const bLastName = (b.provider?.last_name || '').toLowerCase();
                  return aLastName.localeCompare(bLastName);
                })
                .map(renderMember)}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Add Provider Dialog - Find or Add flow matching Streamlit */}
      <Dialog open={addDialogOpen} onClose={handleCloseAddDialog} maxWidth="md" fullWidth>
        <DialogTitle>Find or Add Provider</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" mb={2}>
            Search by name to find existing providers or discover new ones from NPI Registry.
          </Typography>

          {/* Search inputs - Last Name required, First Name and State optional */}
          <Stack direction="row" spacing={2} mb={2}>
            <TextField
              size="small"
              label="Last Name *"
              value={searchLastName}
              onChange={(e) => setSearchLastName(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleProviderSearch()}
              sx={{ flex: 2 }}
            />
            <TextField
              size="small"
              label="First Name"
              value={searchFirstName}
              onChange={(e) => setSearchFirstName(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleProviderSearch()}
              sx={{ flex: 2 }}
            />
            <TextField
              size="small"
              label="State"
              value={searchState}
              onChange={(e) => setSearchState(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleProviderSearch()}
              inputProps={{ maxLength: 2 }}
              sx={{ flex: 1 }}
            />
            <Button
              variant="contained"
              onClick={handleProviderSearch}
              disabled={searching || !searchLastName.trim()}
              startIcon={searching ? <CircularProgress size={16} /> : <SearchIcon />}
            >
              Search
            </Button>
          </Stack>

          {/* Search Results */}
          {hasSearched && !selectedProvider && (
            <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
              {/* Step 1: Local Database Results */}
              <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
                Local Database Results
              </Typography>
              {localSearchResults.length > 0 ? (
                <Box sx={{ mb: 2 }}>
                  <Alert severity="success" sx={{ mb: 1 }}>
                    Found {localSearchResults.length} matching providers in local database
                  </Alert>
                  <List dense>
                    {localSearchResults.map((provider) => (
                      <ListItem
                        key={provider.id}
                        component="div"
                        onClick={() => handleSelectLocalProvider(provider)}
                        sx={{
                          cursor: 'pointer',
                          borderRadius: 1,
                          border: '1px solid',
                          borderColor: 'divider',
                          mb: 0.5,
                          '&:hover': { backgroundColor: 'action.hover' },
                        }}
                      >
                        <ListItemText
                          primary={
                            <Typography variant="body2" fontWeight={500}>
                              {provider.display_name || `${provider.first_name} ${provider.last_name}`}
                              {provider.credential && `, ${provider.credential}`}
                            </Typography>
                          }
                          secondary={
                            <>
                              <span>NPI: {provider.npi}</span>
                              {provider.specialty && <span> • {provider.specialty}</span>}
                            </>
                          }
                        />
                        <Button size="small" variant="outlined">Select</Button>
                      </ListItem>
                    ))}
                  </List>
                </Box>
              ) : (
                <Alert severity="info" sx={{ mb: 2 }}>
                  No matching providers in local database
                </Alert>
              )}

              {/* Step 2: NPI Registry Results */}
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                NPI Registry Results
              </Typography>
              {npiSearchResults.length > 0 ? (
                <Box>
                  <Alert severity="success" sx={{ mb: 1 }}>
                    Found {npiSearchResults.length} providers in NPI Registry
                  </Alert>
                  <List dense>
                    {npiSearchResults.map((result, idx) => {
                      const isInLocalDb = npisInLocalDb.has(result.npi);
                      return (
                        <ListItem
                          key={idx}
                          component="div"
                          onClick={() => !isInLocalDb && handleSelectNpiResult(result)}
                          sx={{
                            cursor: isInLocalDb ? 'default' : 'pointer',
                            borderRadius: 1,
                            border: '1px solid',
                            borderColor: 'divider',
                            mb: 0.5,
                            opacity: isInLocalDb ? 0.7 : 1,
                            '&:hover': { backgroundColor: isInLocalDb ? 'transparent' : 'action.hover' },
                          }}
                        >
                          <ListItemText
                            primary={
                              <Typography variant="body2" fontWeight={500}>
                                {result.entity_type === 'individual'
                                  ? `${result.first_name} ${result.last_name}${result.credential ? `, ${result.credential}` : ''}`
                                  : result.organization_name}
                              </Typography>
                            }
                            secondary={
                              <>
                                <span>NPI: {result.npi}</span>
                                {result.taxonomy_description && <span> • {result.taxonomy_description}</span>}
                                {result.city && result.state && <span> • {result.city}, {result.state}</span>}
                              </>
                            }
                          />
                          {isInLocalDb ? (
                            <Typography variant="caption" color="success.main" fontWeight={500}>
                              ✓ In DB
                            </Typography>
                          ) : (
                            <Button size="small" variant="outlined">Add</Button>
                          )}
                        </ListItem>
                      );
                    })}
                  </List>
                </Box>
              ) : (
                <Alert severity="info">
                  No matching providers found in NPI Registry
                </Alert>
              )}
            </Box>
          )}

          {/* Selected Provider - Show after selection */}
          {selectedProvider && (
            <Box>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" gutterBottom>Selected Provider:</Typography>
              <Card variant="outlined" sx={{ mb: 2 }}>
                <CardContent sx={{ py: 1 }}>
                  <Typography variant="body2" fontWeight={500}>
                    {selectedProvider.first_name} {selectedProvider.last_name}
                    {selectedProvider.credential && `, ${selectedProvider.credential}`}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {selectedProvider.specialty || selectedProvider.taxonomy_display}
                    {selectedProvider.npi && ` • NPI: ${selectedProvider.npi}`}
                  </Typography>
                  {selectedSite && (
                    <Typography variant="caption" color="textSecondary" display="block">
                      Site: {selectedSite.name}, {selectedSite.city}, {selectedSite.state}
                    </Typography>
                  )}
                </CardContent>
              </Card>

              {/* Site search when provider has no linked site */}
              {!selectedSite && (
                <Box sx={{ mb: 2 }}>
                  {/* Informational, not a blocker: the site is optional, so this offers
                      a search rather than demanding one (RD 2026-08-08). */}
                  <Alert severity="info" sx={{ mb: 2 }}>
                    This provider has no linked site. You can search for one below, or add
                    the provider without a site.
                  </Alert>
                  <Box display="flex" gap={1} mb={1}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Search Sites"
                      placeholder="Search by name, address, city..."
                      value={siteSearchQuery}
                      onChange={(e) => setSiteSearchQuery(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleSiteSearch()}
                    />
                    <Button
                      variant="outlined"
                      onClick={handleSiteSearch}
                      disabled={siteSearching || siteSearchQuery.length < 2}
                    >
                      {siteSearching ? <CircularProgress size={20} /> : 'Search'}
                    </Button>
                  </Box>
                  {siteSearchResults.length > 0 && (
                    <Box sx={{ maxHeight: 200, overflow: 'auto', border: 1, borderColor: 'divider', borderRadius: 1 }}>
                      <List dense disablePadding>
                        {siteSearchResults.map((site) => (
                          <ListItem
                            key={site.id}
                            button
                            onClick={() => {
                              setSelectedSite(site);
                              setSiteSearchQuery('');
                              setSiteSearchResults([]);
                            }}
                            sx={{ borderBottom: 1, borderColor: 'divider' }}
                          >
                            <ListItemText
                              primary={site.name}
                              secondary={`${site.address || ''}, ${site.city || ''}, ${site.state || ''} ${site.zip_code || ''}`}
                            />
                          </ListItem>
                        ))}
                      </List>
                    </Box>
                  )}
                </Box>
              )}

              {/* Role selection */}
              <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                <InputLabel>Role</InputLabel>
                <Select
                  value={selectedRole}
                  label="Role"
                  onChange={(e) => setSelectedRole(e.target.value as CareTeamRole)}
                >
                  {CARE_TEAM_ROLES.map((role) => (
                    <MenuItem key={role.value} value={role.value}>
                      {role.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Notes */}
              <TextField
                fullWidth
                size="small"
                label="Notes (optional)"
                multiline
                rows={2}
                value={addNotes}
                onChange={(e) => setAddNotes(e.target.value)}
                sx={{ mt: 2 }}
              />

              {/* Back button to search again */}
              <Button
                variant="text"
                size="small"
                onClick={() => {
                  setSelectedProvider(null);
                  setSelectedSite(null);
                  setSiteSearchQuery('');
                  setSiteSearchResults([]);
                }}
                sx={{ mt: 1 }}
              >
                ← Back to Search Results
              </Button>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAddDialog}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleAddMember}
            disabled={saving || !selectedProvider}
          >
            {saving ? <CircularProgress size={20} /> : 'Add to Care Team'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Member Dialog */}
      <Dialog
        open={editingMember !== null}
        onClose={() => setEditingMember(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Edit Care Team Member</DialogTitle>
        <DialogContent>
          {editingMember && (
            <Box pt={1}>
              <Typography variant="body2" fontWeight={500} gutterBottom>
                {editingMember.provider?.first_name} {editingMember.provider?.last_name}
              </Typography>

              <FormControl fullWidth size="small" sx={{ my: 2 }}>
                <InputLabel>Role</InputLabel>
                <Select
                  value={editRole}
                  label="Role"
                  onChange={(e) => setEditRole(e.target.value as CareTeamRole)}
                >
                  {CARE_TEAM_ROLES.map((role) => (
                    <MenuItem key={role.value} value={role.value}>
                      {role.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                fullWidth
                size="small"
                label="Notes"
                multiline
                rows={2}
                value={editNotes}
                onChange={(e) => setEditNotes(e.target.value)}
                sx={{ mt: 2 }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditingMember(null)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleUpdateMember}
            disabled={saving}
          >
            {saving ? <CircularProgress size={20} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Elation Push Dialog */}
      <ElationPushDialog
        patientId={patientId}
        open={elationPushOpen}
        onClose={() => setElationPushOpen(false)}
      />
    </>
  );
};

export default CareTeamManager;
